/**
 * 사용자 권한(user_level) 공용 유틸.
 *
 * 값: viewer(열람 전용) / user(일반) / admin(관리자).
 * 서버 측 강제는 backend `infrastructure/security/access_policy.py` + 미들웨어가 담당하고,
 * 프런트는 메뉴·버튼 숨김과 라우트 가드로 UX 만 맞춘다.
 */
import { LocalStorage } from 'quasar';

export type UserLevel = 'viewer' | 'user' | 'admin';

export const USER_LEVEL_OPTIONS: { value: UserLevel; label: string; desc: string }[] = [
  { value: 'viewer', label: '열람 전용 (viewer)', desc: '가입 기본값. 데이터·분석 결과 조회만 가능. 업로드·분석 실행·삭제 불가' },
  { value: 'user', label: '일반 사용자 (user)', desc: '본인 데이터 업로드·분석 실행·삭제 (열람 전용 사용자가 권한 변경 신청 → 관리자 승인)' },
  { value: 'admin', label: '시스템 관리자 (admin)', desc: '관리자 메뉴·전체 사용자 데이터 접근' },
];

export function userLevelLabel(level?: string | null): string {
  switch (level) {
    case 'admin': return '관리자';
    case 'viewer': return '열람 전용';
    default: return '사용자';
  }
}

export function userLevelColor(level?: string | null): string {
  if (level === 'admin') return 'red-7';
  if (level === 'viewer') return 'blue-grey-6';
  return 'grey-6';
}

export function getCurrentUser(): Record<string, any> | null {
  try {
    const u = LocalStorage.getItem('user');
    return u && typeof u === 'object' ? (u as Record<string, any>) : null;
  } catch {
    return null;
  }
}

/** 현재 로그인 사용자가 열람 전용(viewer)인지. */
export function isViewer(): boolean {
  return getCurrentUser()?.user_level === 'viewer';
}

/** 업로드·분석 실행·수정·삭제 등 변경 작업이 가능한지(= viewer 가 아님). */
export function canEdit(): boolean {
  return !isViewer();
}

/** viewer 에게 진입 자체를 막는 화면(변경 작업 전용 페이지). */
export const VIEWER_BLOCKED_PATHS = [
  '/data/batch_upload',
  '/data/db_file_designate',
  '/data/db_sync',
  '/quality/add_analysis',
];

export const VIEWER_READONLY_MSG = '열람 전용 계정은 데이터 업로드·분석 실행·수정·삭제를 할 수 없습니다.';
